import React from 'react';

/**
 * BrandMark — emblema de ZEROVEKTOR.
 *
 * La idea: un vector que atraviesa un cero. El anillo es el "zero" (la
 * exposición que se quiere dejar a cero) y la diagonal con punta de flecha es
 * el "vektor" (el camino que sigue un atacante). El vector lo cruza: eso es
 * justo lo que hace la plataforma, enseñar por dónde te entran.
 *
 * Decisiones de dibujo, para que no se rompa al reutilizarlo:
 *  · `currentColor` en el trazo del anillo, así hereda el color del contexto
 *    (sidebar, login, informe) sin tener que pasar props de color.
 *  · El vector usa el acento de MARCA (`--v4-brand`), nunca el rojo: el rojo
 *    en este producto significa riesgo, y un logotipo no es una alarma.
 *  · Sin degradados ni sombras en el SVG: a 20 px se convierten en barro.
 *    El brillo opcional va fuera, con `glow`.
 *  · `viewBox` cuadrado y trazos de grosor constante: legible a 20 px (menú)
 *    y a 96 px (login) sin ajustes.
 */
export default function BrandMark({ size = 32, glow = false, className = '', title = 'ZEROVEKTOR' }) {
  const id = React.useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      role="img"
      aria-label={title}
      className={className}
      style={glow ? { filter: 'drop-shadow(0 0 10px hsl(var(--v4-brand) / 0.45))' } : undefined}
    >
      <title>{title}</title>
      <defs>
        {/* El anillo se recorta por donde pasa el vector, para que se vea que
            lo ATRAVIESA y no que está pegado encima. */}
        <mask id={`${id}-corte`}>
          <rect width="48" height="48" fill="white" />
          <path d="M6 42 L42 6" stroke="black" strokeWidth="9" strokeLinecap="round" />
        </mask>
      </defs>

      {/* El cero, grande: a 24 px tiene que seguir leyéndose como un cero.
          (La primera versión metía además un escudo hexagonal alrededor y con
          tres elementos a ese tamaño el resultado parecía una señal de
          prohibido, no un logotipo. Un elemento menos, legibilidad de más.) */}
      <circle
        cx="24"
        cy="24"
        r="13.5"
        stroke="currentColor"
        strokeOpacity="0.92"
        strokeWidth="3"
        mask={`url(#${id}-corte)`}
      />

      {/* El vector que lo atraviesa, de esquina a esquina y con la punta
          FUERA del anillo: así se ve que sale, que es lo que cuenta. */}
      <path
        d="M9 39 L37 11"
        stroke="hsl(var(--v4-brand))"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <path
        d="M27.5 10 H38.5 V21"
        stroke="hsl(var(--v4-brand))"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Marca completa: emblema + nombre. El `tagline` es opcional porque en el
 * menú lateral no cabe y en el login sí.
 */
export function BrandLockup({ size = 32, tagline = null, glow = false, className = '' }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <BrandMark size={size} glow={glow} className="text-v4-txt shrink-0" />
      <div className="flex items-baseline gap-2 min-w-0">
        <span
          className="font-semibold text-v4-txt tracking-tight leading-none"
          style={{ fontSize: Math.round(size * 0.62) }}
        >
          ZERO<span className="text-v4-brand">VEKTOR</span>
        </span>
        {tagline ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-v4-dim whitespace-nowrap">
            {tagline}
          </span>
        ) : null}
      </div>
    </div>
  );
}
